import Link from "next/link";
import React from "react";
import { tv } from "tailwind-variants";

const buttonVariants = tv({
  base: `flex
            bg-[linear-gradient(45deg,rgba(117,81,203,0)_25%,rgba(255,255,255,.2)_50%,rgba(117,81,203,0)_75%,rgba(117,81,203,0)_100%)]
            bg-[length:250%_250%,100%_100%]
            bg-[position:-100%_0,0_0]
            bg-no-repeat
            hover:bg-[position:200%_0,0_0] hover:duration-[1500ms] hover:-translate-y-0.5 duration-[250ms]
            box-border rounded items-center justify-center font-bold rounded-b-lg border-b-[6px] border-t-[1px]
            border-s-[1px] border-e-[1px] disabled:opacity-10`,
  variants: {
    color: {
      primary: "border-purple-200 bg-brand-slate ",
      brandFree: "border-white-100 bg-brand-slate ",
      brandPremium: "border-blue-500 bg-blue-400 ",
      brandUltimate: "border-purple-300 bg-purple-100",
    },
    size: {
      xxl: "h-11 w-[95%]",
      lg: "h-[55px] w-[315px] text-xl",
      md: "h-[45px] w-[218px]",
      sm: "h-[38px] w-[132px]",
    },
  },
  defaultVariants: {
    color: "primary",
    size: "xxl",
  },
});

interface ButtonProps {
  buttonText: string;
  buttonURL?: string;
  isDisabled?: boolean;
  size: "xxl" | "md" | "sm" | "lg";
  color: "primary" | "brandFree" | "brandPremium" | "brandUltimate";
  onClick?: () => void;
}

interface LinkButtonProps {
  buttonText: string;
  buttonURL: string;
  size: "xxl" | "md" | "sm" | "lg";
  color: "primary" | "brandFree" | "brandPremium" | "brandUltimate";
}

const LinkButton = ({ buttonText, buttonURL, size, color }: LinkButtonProps): React.JSX.Element => (
  <Link
    href={buttonURL}
    style={{ textDecoration: "none" }}
    className={buttonVariants({ color, size })}
  >
    <p className="text-white">{buttonText}</p>
  </Link>
);

interface ActionButtonProps {
  buttonText: string;
  isDisabled?: boolean;
  size: "xxl" | "md" | "sm" | "lg";
  color: "primary" | "brandFree" | "brandPremium" | "brandUltimate";
  onClick?: () => void;
}

const ActionButton = ({
  buttonText,
  isDisabled,
  size,
  color,
  onClick,
}: ActionButtonProps): React.JSX.Element => (
  <button
    disabled={isDisabled}
    onClick={onClick}
    style={{ textDecoration: "none" }}
    className={buttonVariants({ color, size })}
    type="submit"
  >
    <p className="text-white">{buttonText}</p>
  </button>
);

const Button = ({
  buttonText,
  buttonURL,
  isDisabled,
  color,
  size,
  onClick,
}: ButtonProps): React.JSX.Element => {
  return buttonURL ? (
    <LinkButton buttonText={buttonText} buttonURL={buttonURL} color={color} size={size} />
  ) : (
    <ActionButton
      buttonText={buttonText}
      color={color}
      size={size}
      isDisabled={isDisabled}
      onClick={onClick}
    />
  );
};

export default Button;
